import HelperMixin from '../../Shared/HelperMixin.vue';

// SCH-16 / UI-12 — Monthly invoices per school (§8.3).
// Backed by GET api/admin/Schools/{id}/Invoices (SchoolsController.GetSchoolInvoices)
// which returns an array of items carrying a `kind` discriminator:
//   • kind="monthly"      — usage-based monthly invoice: { id, schoolId, periodYear,
//                            periodMonth, activeStudents, ratePerStudent, totalAmount,
//                            status (1=Unpaid,2=Paid,3=Cancelled), createdOn, paidOn, note }.
//   • kind="subscription" — S02-008 subscription invoice (Subscription table):
//                            { id, schoolId, periodYear, periodMonth, totalAmount (=Price),
//                            status (1=active,2=expiry,3=stopped,4=out), startDate, endDate,
//                            createdOn, note }.
// We map those backend field names onto the template's expected names below.

export default {
    name: 'SchoolInvoices',
    mixins: [HelperMixin],

    async created() {
        this.loginDetails = await this.CheckLoginStatus();
        if (!this.requireUserType([1])) return;
        this.GetAllSchools();
    },

    data() {
        return {
            Items: [],
            Schools: [],
            SchoolId: null,
            // الاشتراك الشهري لكل طالب للمدرسة المختارة (null = غير محدَّد،
            // فالوظيفة الشهرية لن تولّد لها أي فاتورة).
            SchoolRate: null,
            From: '',
            To: '',
            MissingEndpoint: false,
        };
    },

    computed: {
        SelectedSchoolName() {
            const s = (this.Schools || []).find(x => x.id === this.SchoolId);
            return s ? s.name : null;
        },
        SchoolHasNoRate() {
            return !!this.SchoolId && (this.SchoolRate == null || Number(this.SchoolRate) <= 0);
        }
    },

    methods: {
        // النوع — distinguishes usage-based monthly invoices from subscription invoices.
        KindLabel(it) {
            return (it && it.kind === 'subscription') ? 'اشتراك' : 'فاتورة شهرية';
        },
        // Status is kind-aware: monthly invoices use 1=Unpaid,2=Paid,3=Cancelled;
        // subscription rows use 1=active,2=expiry,3=stopped,4=out.
        StatusLabel(s, kind) {
            if (kind === 'subscription') {
                if (s === 1) return 'فعّال';
                if (s === 2) return 'منتهٍ';
                if (s === 3) return 'موقوف';
                if (s === 4) return 'خارج';
                return '—';
            }
            if (s === 2 || s === 'paid') return 'مدفوعة';
            if (s === 1 || s === 'pending' || s === 'unpaid') return 'مستحقة';
            if (s === 3 || s === 'cancelled') return 'ملغاة';
            return '—';
        },
        StatusClass(s, kind) {
            if (kind === 'subscription') {
                if (s === 1) return 'pill-paid';      // active
                if (s === 2 || s === 3) return 'pill-pending'; // expiry / stopped
                if (s === 4) return 'pill-overdue';   // out
                return '';
            }
            if (s === 2 || s === 'paid') return 'pill-paid';
            if (s === 1 || s === 'pending' || s === 'unpaid') return 'pill-pending';
            if (s === 3 || s === 'cancelled') return 'pill-overdue';
            return '';
        },
        GetAllSchools() {
            this.$http.GetAllSchools()
                .then(response => {
                    this.Schools = response.data.info || [];
                })
                .catch(() => {
                    this.Schools = [];
                });
        },
        async Load() {
            this.SchoolRate = null;
            if (!this.SchoolId) {
                this.Items = [];
                return;
            }
            this.$blockUI.Start();
            this.MissingEndpoint = false;
            try {
                const from = this.From ? this.ChangeDate(this.From) : '';
                const to = this.To ? this.ChangeDate(this.To) : '';
                const res = await this.$http.GetSchoolInvoices(this.SchoolId, from, to, 1, 50);
                const data = res.data || [];
                const rows = Array.isArray(data) ? data : (data.items || data.info || []);
                // Map backend field names -> the names the template binds to.
                this.Items = rows.map(x => Object.assign({}, x, {
                    invoiceNo: x.invoiceNo || x.id,
                    periodLabel: x.periodLabel || (x.periodYear
                        ? (x.periodYear + '/' + ('0' + x.periodMonth).slice(-2))
                        : (x.month || '—')),
                    studentCount: x.studentCount != null ? x.studentCount : x.activeStudents,
                }));
            } catch (e) {
                const code = e?.response?.status;
                if (code === 404 || code === 405) this.MissingEndpoint = true;
                this.Items = [];
            } finally {
                this.$blockUI.Stop();
            }
            // اشتراك المدرسة الشهري — لعرض تنبيه واضح لو لم يُحدَّد (بدونه
            // لن تتولّد أي فاتورة شهرية). فشل الجلب لا يُفشل تحميل القائمة.
            try {
                const sres = await this.$http.GetSchoolById(this.SchoolId);
                this.SchoolRate = sres.data ? sres.data.monthlyRatePerStudent : null;
            } catch (e) {
                this.SchoolRate = null;
            }
        },
        async GenerateCurrentMonth() {
            if (!this.SchoolId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'يرجى اختيار المدرسة أولاً');
                return;
            }
            this.$blockUI.Start();
            try {
                const res = await this.$http.RefreshSchoolInvoice(this.SchoolId);
                const info = res.data || {};
                if (!info.ratePerStudent || Number(info.ratePerStudent) <= 0) {
                    this.$helper.ShowMessage('warning', 'تنبيه', 'لم يُحدَّد اشتراك شهري لهذه المدرسة');
                } else {
                    this.$helper.ShowMessage('success', 'عملية ناجحة',
                        info.created ? 'تم توليد فاتورة الشهر الحالي' : 'تم تحديث فاتورة الشهر الحالي');
                }
            } catch (e) {
                const msg = e?.response?.data;
                this.$helper.ShowMessage('error', 'خطأ',
                    typeof msg === 'string' && msg ? msg : 'حدث خطأ غير متوقع');
            } finally {
                this.$blockUI.Stop();
            }
            await this.Load();
        }
    }
};
